import React, { ReactNode } from 'react';
import { Button } from 'reactstrap';
import BaseModal from 'modules/Layout/component/Modal';
import Loader from 'modules/Layout/component/Loader';
import InformationList from 'modules/Layout/component/InformationList';
import {
  addToastAction,
  AddToastAction,
  AddToastPayload
} from 'modules/Layout/action';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { AxiosResponse } from 'axios';
import News from 'modules/News/model/News';
import {
  deleteNewsToastError,
  deleteNewsToastSuccess
} from 'modules/News/toasts';
import { deleteNewsDetails } from '../../../repository';

export interface DispatchProps {
  addToast: (payload: AddToastPayload) => AddToastAction;
}

export interface Props extends DispatchProps {
  onSuccess?: (response: AxiosResponse) => void;
}

export interface State {
  isOpen: boolean;
  news: News;
  fetching: boolean;
}

export const mapDispatch = (dispatch: Dispatch): DispatchProps => ({
  addToast: (payload) => dispatch(addToastAction(payload))
});

export class DeleteNewsModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isOpen: false,
      news: null,
      fetching: false
    };

    this.openModal = this.openModal.bind(this);
    this.toggle = this.toggle.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.renderBody = this.renderBody.bind(this);
    this.renderTitle = this.renderTitle.bind(this);
    this.renderFooter = this.renderFooter.bind(this);
  }

  openModal(news: News): void {
    this.setState({
      isOpen: true,
      fetching: false,
      news
    });
  }

  toggle(): void {
    this.setState({
      isOpen: false
    });
  }

  async onSubmit(): Promise<void> {
    const { onSuccess, addToast } = this.props;
    const { news } = this.state;

    this.setState({
      fetching: true
    });

    try {
      const response = await deleteNewsDetails(news.id);

      addToast(deleteNewsToastSuccess());

      this.setState({
        isOpen: false
      });

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.log(error);
      addToast(deleteNewsToastError());
    }
  }

  renderTitle = (): React.ReactNode => {
    return 'Do you want to delete news?';
  };

  renderBody(): React.ReactNode {
    const { fetching, news } = this.state;

    const fields = [
      {
        label: 'Title:',
        value: news.title
      },
      {
        label: 'Description:',
        value: news.description
      },
      {
        label: 'Link:',
        value: news.url
      }
    ];

    return (
      <>
        {fetching && <Loader />}
        <div className="d-flex justify-content-center mb-3">
          {Boolean(news.image) && (
            // eslint-disable-next-line jsx-a11y/alt-text
            <img src={news.image} style={{ height: '150px' }} />
          )}
        </div>
        <InformationList fields={fields} />
      </>
    );
  }

  renderFooter(): React.ReactNode {
    const { fetching } = this.state;

    return (
      <div className="d-flex justify-content-between w-100">
        <Button disabled={fetching} color="primary" onClick={this.toggle}>
          Cancel
        </Button>
        <Button disabled={fetching} color="danger" onClick={this.onSubmit}>
          Delete
        </Button>
      </div>
    );
  }

  render(): ReactNode {
    const { isOpen, news } = this.state;

    if (!news) {
      return null;
    }

    return (
      <BaseModal
        isOpen={isOpen}
        title={this.renderTitle()}
        body={this.renderBody()}
        footer={this.renderFooter()}
        toggle={this.toggle}
      />
    );
  }
}

export default connect<null, DispatchProps>(null, mapDispatch, null, {
  forwardRef: true
})(DeleteNewsModal);
